import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  buildWorkspaceOptions,
  resolveCurrentWorkspace,
} from "state/slices/workspaceSlice";

const formatRoleLabel = (value) => {
  const normalized = String(value || "member").trim();
  if (!normalized) return "Member";

  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatWorkspaceTypeLabel = (value) => {
  if (value === "organization") return "Organization workspace";
  return "Personal workspace";
};

const formatPlatformRoleLabel = (value) => {
  const normalized = String(value || "user").trim();
  if (!normalized) return "User";

  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const buildInitials = (user) => {
  const first = String(user?.firstName || "").trim();
  const last = String(user?.lastName || "").trim();
  const email = String(user?.email || "").trim();

  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }

  return email.slice(0, 2).toUpperCase() || "CD";
};

const readOnlyFieldSx = {
  "& .MuiInputLabel-root": {
    color: "rgba(223, 229, 255, 0.76)",
  },
  "& .MuiOutlinedInput-root": {
    color: "#f8fbff",
    backgroundColor: "rgba(11, 18, 48, 0.72)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.14)",
  },
};

const summaryCardSx = {
  height: "100%",
  borderRadius: 2,
  p: 1.5,
  border: "1px solid rgba(255, 255, 255, 0.1)",
  backgroundColor: "rgba(11, 18, 48, 0.54)",
};

const ProfileTab = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const workspaceState = useSelector((state) => state.workspace);

  const organizationWorkspaces = useMemo(
    () => workspaceState.organizations || [],
    [workspaceState.organizations],
  );
  const workspaceOptions = useMemo(
    () => buildWorkspaceOptions({ user, organizations: organizationWorkspaces }),
    [organizationWorkspaces, user],
  );
  const currentWorkspace = useMemo(
    () =>
      resolveCurrentWorkspace({
        selectedWorkspaceKey: workspaceState.selectedWorkspaceKey,
        user,
        organizations: organizationWorkspaces,
      }),
    [organizationWorkspaces, user, workspaceState.selectedWorkspaceKey],
  );

  const displayName =
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.email || "CityDigitalTwin User";

  const isPersonalWorkspace = currentWorkspace?.type !== "organization";

  return (
    <div className="settings-section">
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Profile
          </Typography>
          <Typography variant="body1" sx={{ color: "rgba(223, 229, 255, 0.82)" }}>
            View your personal account details, current workspace context, and communication status.
          </Typography>
        </Box>

        <Box
          sx={{
            borderRadius: 2,
            p: { xs: 2, md: 2.5 },
            border: "1px solid rgba(255, 255, 255, 0.1)",
            background: "linear-gradient(135deg, rgba(14, 24, 64, 0.92), rgba(35, 46, 102, 0.9))",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  fontSize: 24,
                  fontWeight: 700,
                  color: "#081227",
                  backgroundColor: "#8fdcff",
                }}
              >
                {buildInitials(user)}
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                  {displayName}
                </Typography>
                <Typography variant="body1" sx={{ color: "rgba(223, 229, 255, 0.82)" }}>
                  {user?.email || "No email available"}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1, flexWrap: "wrap" }}>
                  <Chip
                    label={formatWorkspaceTypeLabel(currentWorkspace?.type)}
                    size="small"
                    sx={{ color: "#f8fbff", borderColor: "rgba(255,255,255,0.18)" }}
                    variant="outlined"
                  />
                  <Chip
                    label={user?.marketingOptIn ? "Marketing emails enabled" : "Marketing emails off"}
                    size="small"
                    color={user?.marketingOptIn ? "success" : "default"}
                    variant="outlined"
                  />
                  <Chip
                    label={user?.mfaEnabled ? "MFA enabled" : "MFA not enabled"}
                    size="small"
                    color={user?.mfaEnabled ? "success" : "warning"}
                    variant="outlined"
                  />
                  <Chip
                    label={`Platform role: ${formatPlatformRoleLabel(user?.role)}`}
                    size="small"
                    color={user?.role === "super_admin" ? "secondary" : "default"}
                    variant="outlined"
                  />
                </Stack>
              </Box>
            </Stack>

            <Box sx={{ minWidth: { xs: "100%", md: 240 } }}>
              <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                Current workspace
              </Typography>
              <Typography variant="body1" sx={{ color: "#f8fbff", fontWeight: 700, mt: 0.35 }}>
                {currentWorkspace?.name || "Personal workspace"}
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)", mt: 0.25 }}>
                {currentWorkspace?.description || "Your personal projects and demo access"}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} xl={3}>
            <Box sx={summaryCardSx}>
              <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                Account ID
              </Typography>
              <Typography variant="body1" sx={{ color: "#f8fbff", fontWeight: 700, mt: 0.35 }}>
                {user?._id || user?.id || "Not available"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} xl={3}>
            <Box sx={summaryCardSx}>
              <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                Workspace mode
              </Typography>
              <Typography variant="body1" sx={{ color: "#f8fbff", fontWeight: 700, mt: 0.35 }}>
                {formatWorkspaceTypeLabel(currentWorkspace?.type)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} xl={3}>
            <Box sx={summaryCardSx}>
              <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                Organization access
              </Typography>
              <Typography variant="body1" sx={{ color: "#f8fbff", fontWeight: 700, mt: 0.35 }}>
                {organizationWorkspaces.length}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} xl={3}>
            <Box sx={summaryCardSx}>
              <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                Platform role
              </Typography>
              <Typography variant="body1" sx={{ color: "#f8fbff", fontWeight: 700, mt: 0.35 }}>
                {formatPlatformRoleLabel(user?.role)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} xl={3}>
            <Box sx={summaryCardSx}>
              <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                Communication status
              </Typography>
              <Typography variant="body1" sx={{ color: "#f8fbff", fontWeight: 700, mt: 0.35 }}>
                {user?.marketingOptIn ? "Subscribed" : "Essential emails only"}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.08)" }} />

        <Grid container spacing={3}>
          <Grid item xs={12} lg={7}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                  Personal details
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.74)", mt: 0.5 }}>
                  These fields reflect your current account identity. Editable profile controls can be added here next.
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="First name"
                    value={user?.firstName || ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={readOnlyFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Last name"
                    value={user?.lastName || ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={readOnlyFieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Email address"
                    value={user?.email || ""}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={readOnlyFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Personal workspace"
                    value={workspaceOptions[0]?.name || "Personal workspace"}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={readOnlyFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Current workspace"
                    value={currentWorkspace?.name || "Personal workspace"}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={readOnlyFieldSx}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                  Workspace summary
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.74)", mt: 0.5 }}>
                  Quick context for where personal plans apply and where organization settings take over.
                </Typography>
              </Box>
              <Box sx={summaryCardSx}>
                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                  Personal workspace scope
                </Typography>
                <Typography variant="body2" sx={{ color: "#f8fbff", mt: 0.75 }}>
                  Personal billing, personal projects, and personal plan usage are tied to your personal workspace only.
                </Typography>
              </Box>
              <Box sx={summaryCardSx}>
                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                  Organization workspace scope
                </Typography>
                <Typography variant="body2" sx={{ color: "#f8fbff", mt: 0.75 }}>
                  Organization billing, members, invites, budgets, and plan controls live under the selected organization workspace.
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.08)" }} />

        <Box>
          <Typography variant="h6" sx={{ color: "#f8fbff", fontWeight: 700 }}>
            How workspaces work
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.74)", mt: 0.5, mb: 2 }}>
            Personal workspace remains your default home. Organization workflows are optional and only apply when you intentionally switch into an organization workspace.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={summaryCardSx}>
                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                  Billing context
                </Typography>
                <Typography variant="body2" sx={{ color: "#f8fbff", mt: 0.75 }}>
                  Personal billing applies in personal workspace. Organization billing, credits, and limits only apply after you switch into an organization workspace.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={summaryCardSx}>
                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                  Project move options
                </Typography>
                <Typography variant="body2" sx={{ color: "#f8fbff", mt: 0.75 }}>
                  Copy keeps your personal project in place and creates an organization-owned duplicate. Transfer moves the existing project into the selected organization workspace.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={summaryCardSx}>
                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                  Organization settings
                </Typography>
                <Typography variant="body2" sx={{ color: "#f8fbff", mt: 0.75 }}>
                  Use organization settings to manage members, invites, budgets, billing, and ownership controls for the active organization workspace.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={summaryCardSx}>
                <Typography variant="caption" sx={{ color: "rgba(223, 229, 255, 0.72)" }}>
                  Where to go next
                </Typography>
                <Typography variant="body2" sx={{ color: "#f8fbff", mt: 0.75 }}>
                  Open Organizations to manage memberships and workspace setup. Open Projects when you are ready to copy or transfer a personal project into an organization.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => navigate("/settings?tab=organizations")}
              sx={{ textTransform: "none", fontWeight: 700, alignSelf: "flex-start" }}
            >
              Open Organizations
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/projects")}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "#f8fbff",
                borderColor: "rgba(248, 250, 252, 0.3)",
                alignSelf: "flex-start",
                "&:hover": {
                  borderColor: "rgba(248, 250, 252, 0.56)",
                  backgroundColor: "rgba(248, 250, 252, 0.06)",
                },
              }}
            >
              {isPersonalWorkspace ? "Open Projects" : "Open Projects in Current Workspace"}
            </Button>
          </Stack>
        </Box>

        <Box>
          <Typography variant="h6" sx={{ color: "#f8fbff", fontWeight: 700 }}>
            Organization memberships
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.74)", mt: 0.5, mb: 2 }}>
            The organizations you can switch into from the workspace selector.
          </Typography>

          {organizationWorkspaces.length === 0 ? (
            <Box sx={summaryCardSx}>
              <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.78)" }}>
                You currently only have a personal workspace.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {organizationWorkspaces.map((organization) => (
                <Grid item xs={12} md={6} key={organization._id}>
                  <Box sx={summaryCardSx}>
                    <Stack spacing={1}>
                      <Box>
                        <Typography variant="body1" sx={{ color: "#f8fbff", fontWeight: 700 }}>
                          {organization.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "rgba(223, 229, 255, 0.76)" }}>
                          /{organization.slug}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                        <Chip
                          label={formatRoleLabel(organization.membership?.orgRole)}
                          size="small"
                          variant="outlined"
                          color="success"
                        />
                        <Chip
                          label={organization.membership?.status || "active"}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Stack>
    </div>
  );
};

export default ProfileTab;
